import type { Completion, JobInitiator, JobStatus, LandingStatus, MessageJson, TaskJson } from '../types/models.ts'

export type ResultTone = 'neutral' | 'success' | 'warning' | 'danger'
export interface ResultState { label: string | null; tone: ResultTone }

export function taskAction(task: Pick<TaskJson, 'approval' | 'browser_login' | 'question' | 'status'>): string {
  if (task.approval) return 'Review approval'
  if (task.browser_login) return 'Continue sign-in'
  if (task.question) return 'Answer question'
  return task.status === 'todo' ? 'Open' : 'Review'
}

const LANDING_TEXT: Partial<Record<LandingStatus, string>> = {
  deferred: 'Not happened yet',
  dropped: 'Did not happen',
  superseded: 'Replaced by a later plan',
  obsolete: 'No longer needed',
}

export function taskPreview(task: Pick<TaskJson, 'status' | 'summary' | 'preview' | 'question' | 'status_reason'> & Partial<Pick<TaskJson, 'blocked_on' | 'landing'>>): string | null {
  if (task.status === 'done') {
    const landing = task.landing ? LANDING_TEXT[task.landing.status] : undefined
    if (landing) return task.landing?.note ? `${landing}: ${task.landing.note}` : landing
    return task.summary || task.preview || 'Open to see the outcome.'
  }
  if (task.status === 'needs_you') return task.question || task.status_reason || task.preview
  const blocked = task.blocked_on?.note || task.blocked_on?.title
  if (blocked) return `Waiting on ${blocked}`
  return task.status_reason || task.preview
}

// Delivery text includes an author/status prefix for plain-text channels. Chat
// already shows both, so render just the result without changing stored messages.
export function resultText(text = '', authorName?: string | null): string {
  if (authorName && text.startsWith(`${authorName}: `)) text = text.slice(authorName.length + 2)
  const prefix = /^✅ [^\r\n]+? finished(?::\s*|$)/u
  if (!prefix.test(text)) return text
  const summary = text.replace(prefix, '')
  return summary.trim().toLowerCase() === 'done' ? '' : summary
}

export function resultState(status: JobStatus | null | undefined, handoff?: string | null, completion?: Completion): ResultState {
  if (handoff === 'approved' || handoff === 'answered') return { label: null, tone: 'neutral' }
  if (status === 'succeeded' && completion === 'partial') return { label: null, tone: 'neutral' }
  if (status === 'succeeded' && completion === 'blocked') return { label: 'Could not complete', tone: 'warning' }
  if (status === 'succeeded') return { label: 'Completed', tone: 'success' }
  if (status && ['failed', 'lost'].includes(status)) return { label: 'Could not finish', tone: 'danger' }
  if (status === 'cancelled') return { label: 'Cancelled', tone: 'neutral' }
  if (status === 'waiting_input') return { label: 'Your input needed', tone: 'warning' }
  if (status === 'waiting_approval') return { label: 'Approval needed', tone: 'warning' }
  if (status && ['queued', 'granted', 'running'].includes(status)) return { label: status === 'running' ? 'Working' : 'Queued', tone: 'neutral' }
  return { label: 'Update', tone: 'neutral' }
}

export function retryPresentation(message: MessageJson): { state: ResultState; text: string } | null {
  const work = message.work
  const latestId = work?.current_job_id || work?.latest_job_id
  if (!message.job || !['failed', 'lost'].includes(message.job.status) || !work || !latestId || latestId === message.job.id) return null
  if (work.current_job_status && ['queued', 'granted', 'running'].includes(work.current_job_status)) return {
    state: { label: 'Earlier attempt', tone: 'neutral' },
    text: 'This attempt stopped. Sunny is trying again now; you don’t need to send it again.',
  }
  if (work.current_job_status && ['waiting_input', 'waiting_approval', 'interrupted'].includes(work.current_job_status)) return {
    state: { label: 'Earlier attempt', tone: 'neutral' },
    text: 'This attempt stopped. Sunny tried again and is waiting for your attention further down this conversation.',
  }
  return { state: { label: 'Earlier attempt', tone: 'neutral' }, text: 'This attempt stopped. Sunny tried again; the latest result is further down this conversation.' }
}


export interface TaskCardUpdate { label: string; text: string; tone: ResultTone; at: string }

export function taskCardUpdate(task: TaskJson): TaskCardUpdate {
  if (task.status === 'needs_you') return { label: 'Needs you', text: task.question || task.approval?.reason || task.status_reason || 'Open to continue.', tone: 'warning', at: task.updated_at }
  if (task.current_job_id || ['working', 'waiting'].includes(task.status)) {
    const progress: { text?: string; at?: string } | null = task.progress_update
    const text = progress?.text?.replace(/^(?:Progress|Update) on .+?: /, '')
    return { label: task.status === 'working' ? 'In progress' : 'Waiting', text: text || task.status_reason || 'Sunny is working on this.', tone: 'neutral', at: progress?.at || task.updated_at }
  }
  if (task.outcome && ['failed', 'lost'].includes(task.outcome)) return { label: 'Couldn’t finish', text: task.summary || 'Open to review what happened and try again.', tone: 'danger', at: task.last_run_at || task.updated_at }
  if (task.outcome === 'cancelled') return { label: 'Cancelled', text: task.summary || 'This was stopped.', tone: 'neutral', at: task.last_run_at || task.updated_at }
  if (task.completion === 'blocked' && task.summary) return { label: 'Needs follow-up', text: task.summary, tone: 'warning', at: task.last_run_at || task.updated_at }
  if (task.summary) return { label: task.completion === 'partial' ? 'Findings so far' : 'Latest outcome', text: task.summary, tone: 'neutral', at: task.last_run_at || task.updated_at }
  return { label: task.status === 'done' ? 'Completed' : task.status === 'watching' ? 'Scheduled' : 'To do', text: task.brief || task.preview || 'Open for details.', tone: 'neutral', at: task.updated_at }
}

// Who started a run, for a row read after being away. Nothing for a run a person started: that is
// the default, and saying so on every row would bury the ones worth noticing.
export function initiatorLabel(initiator: JobInitiator | null | undefined): string | null {
  const labels: Partial<Record<JobInitiator, string>> = { schedule: 'On a schedule', system: 'Automatic' }
  return (initiator && labels[initiator]) || null
}
